/* eslint-disable sonarjs/no-duplicate-string */
import React, { Suspense } from "react"
import "react-app-polyfill/ie11"
import "react-app-polyfill/stable"
import { createRoot } from "react-dom/client"
import { ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import "ts-polyfill"
import { create } from "zustand"
import CookieUtils from "./CookieUtils"
import {
    addValidationTextToInput,
    download,
    getCurrentLanguage,
    handleConnectionError,
    handleErrors,
    handleFormats,
    handleLanguageChange,
    hanldeCustomUploadAdvices,
    persistCurrentLanguage,
    sortingAlerts,
} from "./Utils"
import { AppContainer } from "./container"
import { ConfigContext } from "./contexts/CustomConfigContext"
import { EventBus } from "./eventbus"
import i18n from "./i18n"
import { CustomConfig } from "./models/CustomConfig"
import { ErrorDto } from "./models/Validation"
import { Anlage } from "./models/anlage"
import { Rechnung } from "./models/rechnung"
import { SteuerDetails } from "./models/rechnung/rechnungsebene/steuerdetails"
import { Rechnungstext } from "./models/rechnungstext"
import Ueberweisung from "./models/ueberweisung"
import { UploadAnlage } from "./models/uploadanlage"
import { ValidierteXRechnung } from "./models/validierteXRechnung"
import { ConfigService } from "./services/ConfigService"
import { FeatureService } from "./services/FeatureService"
import { emitGlobalChange, emitRegionChange, emitRegionChangeIfAnyMatch, rechnung, setRechnung, store } from "./store"
import { ErrorPage } from "./views/other/errorPage"

declare let jQuery: any

export const useValidierteRechnungStore = create<ValidierteXRechnung | undefined | null>(() => null)

let customConfig: CustomConfig = new CustomConfig()
const container = document.getElementById("app")
const root = createRoot(container!)

const loadConfig = () => {
    return ConfigService.loadConfig()
        .then((data) => {
            if (data) {
                customConfig = data
            }
            return data
        })
        .then((data) => handleFormats(data))
        .then((data) => hanldeCustomUploadAdvices(data))
        .catch(handleConnectionError)
}

FeatureService.getFeatures()
    .then(async () => await loadConfig())

    .then(() => render(false, true))
    .catch(() => render(true, true))

function render(hasError?: boolean, checkLanguageChange?: boolean) {
    const lang = getCurrentLanguage()
    if (checkLanguageChange && i18n.language !== lang) {
        EventBus.instance.emit("languageChanged", lang)
    }
    if (hasError) {
        console.error("Features konnten nicht abgefragt werden.")
        root.render(
            <Suspense fallback={<div>Loading ...</div>}>
                <ErrorPage />
            </Suspense>,
        )
        return
    }

    root.render(
        <Suspense fallback={<div>Loading ...</div>}>
            <ConfigContext.Provider value={customConfig}>
                <AppContainer />
                <ToastContainer />
            </ConfigContext.Provider>
        </Suspense>,
    )
    sortingAlerts()
    handleLanguageChange()
}

/** wenn sich die Rechnung ändert, ist die validierung nicht mehr gültig und sollte zurückgesetzt werden */
function revokeValidationStatus() {
    if (useValidierteRechnungStore.getState()?.validierungErfolgreich) {
        useValidierteRechnungStore.setState(undefined)
        render()
    }
}

EventBus.instance
    .on("changeProperty", (path: string, value: unknown) => {
        store.changeProperty(path, value)
        revokeValidationStatus()
        emitRegionChangeIfAnyMatch(path)
    })

    .on("addItem", (path: string, item: unknown) => {
        store.push(path, item)
        revokeValidationStatus()
        emitRegionChangeIfAnyMatch(path)
    })

    .on("removeItem", (path: string, index: number) => {
        store.remove(path, index)
        revokeValidationStatus()
        emitRegionChangeIfAnyMatch(path)
    })

    .on("addAnlage", (instance: Anlage) => {
        rechnung.anlagen.push(instance)
        revokeValidationStatus()
        emitRegionChange("anlagen")
    })

    .on("removeAnlage", (index: number) => {
        rechnung.removeAnlage(index)
        revokeValidationStatus()
        emitRegionChange("anlagen")
    })

    .on("addRechnungstext", () => {
        rechnung.rechnungstexte.push(new Rechnungstext())
        revokeValidationStatus()
        emitRegionChange("rechnungstexte")
    })

    .on("removeRechnungstext", (index: number) => {
        rechnung.removeRechnungstext(index)
        revokeValidationStatus()
        emitRegionChange("rechnungstexte")
    })

    .on("addUeberweisung", () => {
        rechnung.zahlungsdaten.ueberweisung.push(new Ueberweisung())
        revokeValidationStatus()
        emitRegionChange("zahlungsdaten")
    })

    .on("removeUeberweisung", (index: number) => {
        rechnung.zahlungsdaten.removeUeberweisung(index)
        revokeValidationStatus()
        emitRegionChange("zahlungsdaten")
    })

    .on("addSteuerDetail", () => {
        rechnung.steuerDetails.push(new SteuerDetails())
        revokeValidationStatus()
        emitRegionChange("steuerDetails")
    })

    .on("removeSteuerDetail", (index: number) => {
        rechnung.removeSteuerDetail(index)
        revokeValidationStatus()
        emitRegionChange("steuerDetails")
    })

    .on("loadModel", (model: Rechnung) => {
        setRechnung(model)
        jQuery(".card-body.collapse").addClass("show")
        emitGlobalChange()
    })

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .on("removeVorausgegangeneRechnung", (_: number) => {
        // TODO: vorausgegangen rechnung wieder entfernen
        // rechnung.rechnungsdaten.vorausgegangenerechnungen.removeInvoice(index);
        render()
    })

    .on("addVorausgegangeneRechnung", () => {
        // TODO: vorausgegangen rechnung wieder einfügen
        // rechnung.rechnungsdaten.vorausgegangenerechnungen.rechnungen.push(
        //  new VorausgegangeneRechnung()
        // );
        render()
    })

    .on("renderView", () => {
        render()
    })

    .on("scroll", (to: string) => {
        switch (to) {
            case "top":
                window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                })
                break
            case "bottom":
                window.scrollTo({
                    top: document.body.scrollHeight,
                    behavior: "smooth",
                })
                break
        }
    })

    .on("processValidate", (rechnung: ValidierteXRechnung) => {
        useValidierteRechnungStore.setState(rechnung)
        EventBus.instance.emit("renderFieldErrors", rechnung?.validierungFehler)
    })

    .on("renderFieldErrors", (errors: ErrorDto[] | undefined) => {
        const dangerClass = "text-danger"
        const invalidClass = "is-invalid"

        //reset all
        jQuery("#app div[class=container]").find(`.${invalidClass}`).removeClass(`${invalidClass}`)
        jQuery(`#app div[class=container] small[class=${dangerClass}]`).remove()

        if (!errors) return

        const regexp = /\(BT-.*?\)/g

        errors.forEach((v) => {
            const foundCodes = [...i18n.t(v.code).matchAll(regexp)].flat()
            const foundCodesReduced = new Set(foundCodes)
            foundCodesReduced.forEach((value) => {
                addValidationTextToInput(value, v.code, dangerClass, invalidClass, v.position)
            })
            if (v.code === null || v.code.startsWith("global-")) addValidationTextToInput(v.code, v.fehlermeldung, dangerClass, invalidClass, v.position)
        })
        render()
    })

    .on("visualization", (rechnung: ValidierteXRechnung, onSuccess?: () => void) => {
        const formdata: FormData = new FormData()
        formdata.append("xmlData", rechnung.xmlData)
        const cookie = CookieUtils.get("XSRF-TOKEN")
        fetch(`/api/v1/visualisierung`, {
            method: "POST",
            headers: { "X-XSRF-TOKEN": cookie as string },
            body: formdata,
        })
            .then(handleErrors)
            .then((response) => response.blob())
            .then((data) => window.open(URL.createObjectURL(new Blob([data], { type: "application/pdf" })), "_blank"))
            .catch(handleConnectionError)
            .finally(() => onSuccess?.())
    })

    .on("validate", (rechnung: Rechnung, format: string, onSuccess?: () => void) => {
        const cookie = CookieUtils.get("XSRF-TOKEN")
        fetch(`/api/v1/validate/${format}`, {
            method: "POST",
            headers: { "X-XSRF-TOKEN": cookie as string },
            body: createFormData(rechnung),
        })
            .then(handleErrors)
            .then((response) => response.json())
            .then((data) => EventBus.instance.emit("processValidate", ValidierteXRechnung.fromJSON(data)))
            .catch(handleConnectionError)
            .finally(() => onSuccess?.())
        render()
    })

    .on("creating", (rechnung: Rechnung, format: string, onSuccess?: () => void) => {
        const cookie = CookieUtils.get("XSRF-TOKEN")
        fetch(`/api/v1/caching/${format}`, {
            method: "POST",
            headers: { "X-XSRF-TOKEN": cookie as string },
            body: createFormData(rechnung),
        })
            .then(handleErrors)
            .then((response) => response.json())
            .then((data) => ValidierteXRechnung.fromJSON(data))
            .then((r) => download(new Blob([r.xmlData], { type: "text/xml" }), r.xmlDateiname))
            .catch(handleConnectionError)
            .finally(() => onSuccess?.())
    })
    .on("languageChanged", (language: string | undefined) => {
        useValidierteRechnungStore.setState(undefined)
        if (rechnung && rechnung instanceof ValidierteXRechnung) {
            rechnung.validierungFehler = undefined
        }
        i18n.changeLanguage(language || "de")
        persistCurrentLanguage(language || "de")
        handleLanguageChange()
        removeFormErrors()
        render(false, false)
    })

const removeFormErrors = () => {
    //reset all errors
    const dangerClass = "text-danger"
    const invalidClass = "is-invalid"
    jQuery("#app div[class=container]").find(`.${invalidClass}`).removeClass(`${invalidClass}`)
    jQuery(`#app div[class=container] small[class=${dangerClass}]`).remove()
}
const createFormData = (rechnung: Rechnung): FormData => {
    const formdata: FormData = new FormData()

    rechnung.anlagen
        .filter((a) => a instanceof UploadAnlage)
        .filter((a) => a.notEmpty)
        .map((a) => a as UploadAnlage)
        // für alle neuen nicht leeren uploads
        .forEach((anlage: any, index: number) => {
            // index setzen
            anlage.multipartIndex = index
            // datei zu form hinzufügen
            formdata.append("file[]", anlage.datei, anlage.name)
        })
    // rechnung als json
    formdata.append("rechnung", JSON.stringify(rechnung))
    formdata.append("locale", i18n.language)
    return formdata
}
